import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Database, HeartPulse, Lightbulb, LineChart, MessagesSquare, Play, Rocket, Search, Zap } from 'lucide-react';
import DataNights from '../assets/Data-nights.png';
import hcmvpThumbnail from '../assets/hcmvp_thumbnail.png';
import { PAGES, usePageMeta } from '../seo/seo';

const FEATURES = [
  {
    icon: <Zap />,
    title: 'AI-Powered Excel Automation',
    text: 'Convert slow VBA macros into fast, reusable functions and automate workflows—no IT support needed, directly within Excel.',
    link: '/solutions',
    linkLabel: 'Learn more',
  },
  {
    icon: <Database />,
    title: 'Real-Time Data Integration',
    text: 'Pull market data, reports, and enterprise insights into Excel with one click—no more copy-pasting or manual imports.',
    link: '/solutions',
    linkLabel: 'Explore solutions',
  },
  {
    icon: <MessagesSquare />,
    title: 'Natural Language Data Access',
    text: 'Query enterprise data in plain English and get instant insights—without SQL, coding, or IT delays.',
    link: '/webaiexcel',
    linkLabel: 'See it in action',
  },
];

const STEPS = [
  {
    icon: <Search />,
    title: 'Understand',
    text: 'We start by learning your data, your processes and where your workflow slows down.',
  },
  {
    icon: <Lightbulb />,
    title: 'Prototype',
    text: 'We build a working solution inside your Excel—AI where it helps, smart automation where it’s the better fit.',
  },
  {
    icon: <Rocket />,
    title: 'Refine',
    text: 'We refine it with you until it fits the way you work—before any commitment.',
  },
];

const INDUSTRIES = [
  {
    icon: <LineChart />,
    title: 'WebAIExcel for Finance',
    text: 'Financial analysis, reporting and risk modeling with live data and automation built into Excel.',
    image: 'https://i.ytimg.com/vi/8eil0ujaYig/hqdefault.jpg',
  },
  {
    icon: <HeartPulse />,
    title: 'WebAIExcel for Healthcare',
    text: 'Healthcare data workflows streamlined inside the spreadsheet your teams already rely on.',
    image: hcmvpThumbnail,
  },
];

const HomePage = () => {
  const navigate = useNavigate();
  usePageMeta(PAGES.home);

  const handleDemoButtonClick = () => {
    navigate('/webaiexcel');
  };

  const handleGetStartedButtonClick = () => {
    navigate('/contact');
  };

  const handleLMButtonClick = () => {
    navigate('/blogs');
  };

  return (
    <div className="bg-white dark:bg-gray-900 transition-colors duration-300">
      {/* Hero */}
      <section className="relative min-h-[calc(100vh-4.5rem)] w-full flex items-center overflow-hidden">
        <img src={DataNights} alt="Data analytics landscape" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/10 via-black/30 to-black/70 dark:from-black/30 dark:via-black/50 dark:to-black/80" />

        <div className="relative w-full max-w-7xl mx-auto px-6 md:px-8 py-20">
          <div className="ml-auto w-full md:w-1/2 lg:w-[45%]">
            <p className="inline-block text-sm font-semibold uppercase tracking-widest text-white/90 bg-white/10 backdrop-blur px-3 py-1 rounded-full mb-6">
              WebAIExcel by CognitAIX
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight">
              AI-Powered Excel for Finance & Healthcare Professionals
            </h1>
            <p className="mt-6 text-lg md:text-xl text-gray-100">
              WebAIExcel brings AI & automation into Excel—customized for your unique workflows.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button
                type="button"
                onClick={handleDemoButtonClick}
                className="flex items-center gap-2 px-6 py-3 bg-brandPurple text-white font-semibold rounded-lg shadow-lg hover:bg-opacity-90 transition-all duration-300"
              >
                <Play className="w-5 h-5" /> See how it works
              </button>
              <button
                type="button"
                onClick={handleLMButtonClick}
                className="flex items-center gap-2 px-6 py-3 border border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-300"
              >
                Learn More <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-[#3399FF] dark:text-white">
              WebAIExcel: Excel, Reinvented for Professionals
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
              Keep the tool your team trusts. Add the automation, live data and AI it has been missing.
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            {FEATURES.map((feature) => (
              <Link
                key={feature.title}
                to={feature.link}
                className="group flex flex-col bg-white dark:bg-gray-900 p-8 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-[#36AC98] transition-all duration-300"
              >
                <div className="inline-flex self-start p-3 rounded-xl bg-[#3399FF]/10 text-[#3399FF] dark:text-[#36AC98] mb-6">
                  {React.cloneElement(feature.icon, { size: 28 })}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">{feature.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 flex-1">{feature.text}</p>
                <span className="mt-6 inline-flex items-center gap-1 font-medium text-[#3399FF] group-hover:text-brandPurple dark:group-hover:text-brandBlue transition-colors">
                  {feature.linkLabel} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How we work */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">How We Work With You</h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">No risk, no hype—just a solution shaped around your workflow.</p>
          </div>
          <ol className="grid gap-10 md:grid-cols-3">
            {STEPS.map((step, index) => (
              <li key={step.title} className="relative text-center md:px-6">
                {index < STEPS.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-[calc(50%+3rem)] right-[calc(-50%+3rem)] border-t-2 border-dashed border-gray-200 dark:border-gray-700" />
                )}
                <div className="relative mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-br from-brandPurple to-brandBlue text-white shadow-lg mb-6">
                  {React.cloneElement(step.icon, { size: 26 })}
                </div>
                <p className="text-sm font-semibold text-[#36AC98] mb-1">Step {index + 1}</p>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">{step.title}</h3>
                <p className="text-gray-600 dark:text-gray-300">{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-[#3399FF] dark:text-white">Built for Your Industry</h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">Watch WebAIExcel working on real finance and healthcare workflows.</p>
          </div>
          <div className="grid gap-8 md:grid-cols-2 max-w-5xl mx-auto">
            {INDUSTRIES.map((industry) => (
              <Link
                key={industry.title}
                to="/webaiexcel"
                className="group flex flex-col bg-white dark:bg-gray-900 rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-lg transition-shadow duration-300"
              >
                <div className="relative aspect-video overflow-hidden bg-gray-900">
                  <img
                    src={industry.image}
                    alt=""
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                    <span className="flex items-center justify-center w-14 h-14 rounded-full bg-white/90 text-brandPurple shadow-lg transition-transform group-hover:scale-110">
                      <Play className="w-6 h-6 ml-0.5" fill="currentColor" />
                    </span>
                  </div>
                </div>
                <div className="p-6 flex-1">
                  <h3 className="flex items-center gap-2 text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    <span className="text-[#3399FF] dark:text-[#36AC98]">{React.cloneElement(industry.icon, { size: 22 })}</span>
                    {industry.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">{industry.text}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Call to action */}
      <section className="py-16 bg-[#3399ff]">
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Why keep struggling? Supercharge your Excel today!</h2>
          <p className="text-lg text-white mb-8 max-w-3xl mx-auto">
            Built by finance professionals who know your pain, WebAIExcel bridges the gap between Excel and modern AI & web technologies.
          </p>
          <button
            type="button"
            onClick={handleGetStartedButtonClick}
            className="px-8 py-3 bg-white text-[#3399FF] font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300"
          >
            Schedule a Demo
          </button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
